/* eslint @typescript-eslint/strict-boolean-expressions: off */

import React from "react";
import styles from "gui/entries/favorites.module.css";
import { EntryDashboard, EntryQuery } from "lib/entries/types";
import { Icon } from "gui/icon/icon";
import { formatNumber } from "lib/intl/number";
import { SessionContext } from "gui/session/session";
import { logger } from "lib/logger/browser";
import { updateFavorite } from "lib/entries/entries";
import { useEditorQuery } from "gui/editor/context";
import { Button } from "components/Button/Button";
import { Tooltip } from "components/Tooltip/Tooltip";

interface Props {
  entry: EntryDashboard | EntryQuery;
  readOnly?: boolean;
}

export const EntryFavoritesText: React.FC<Props> = (props) => {
  const { session } = React.useContext(SessionContext);
  const [favorite, count, onClick] = useFavoriteCount(props.entry);

  if (!session || props.readOnly) {
    return (
      <div className={styles.text}>
        {formatNumber(count)}
        <Icon icon="person" />
      </div>
    );
  }

  return (
    <div className={styles.text}>
      {formatNumber(count)}
      <button type="button" onClick={onClick} aria-label={ariaLabel}>
        <Icon icon={favorite ? "star-fill" : "star"} />
      </button>
    </div>
  );
};

export const EntryFavoritesButton: React.FC<Props> = (props) => {
  const { session } = React.useContext(SessionContext);
  const [favorite, count, onClick] = useFavoriteCount(props.entry);
  const query = useEditorQuery();

  if (!session || props.readOnly) {
    return (
      <Button
        size="M"
        theme="tertiary"
        aria-label={ariaLabel}
        className={styles.readOnlyBtn}
      >
        <Icon icon="star" />
        {formatNumber(count)}
      </Button>
    );
  }

  return (
    <Tooltip
      position="below-center"
      label={
        query
          ? "Archived queries cannot be favorited"
          : "Archived dashboards cannot be favorited"
      }
      hidden={!props.entry?.is_archived}
    >
      <Button
        {...props}
        onClick={onClick}
        aria-label={ariaLabel}
        disabled={props.entry?.is_archived}
        theme={"tertiary"}
        size="M"
      >
        <Icon icon={favorite ? "star-fill" : "star"} />
        {formatNumber(count)}
      </Button>
    </Tooltip>
  );
};

// Copy the entry favorite state and favorite count to local state
// so that we can manipulate them directly without re-fetching data.
const useFavoriteCount = (
  entry: EntryDashboard | EntryQuery
): [boolean, number, () => void] => {
  const { session } = React.useContext(SessionContext);
  const [favorite, setFavorite] = React.useState(entry.is_favorite);
  const [count, setCount] = React.useState(entry.num_favorites_in_period);

  React.useEffect(() => {
    setFavorite(entry.is_favorite);
    setCount(entry.num_favorites_in_period);
  }, [entry]);

  const onClick = () => {
    updateFavorite(entry, !favorite, session!).catch(logger.warn);
    setCount(count + (favorite ? -1 : 1));
    setFavorite(!favorite);
  };

  return [favorite, count, onClick];
};

const ariaLabel = "Toggle favorite";
